import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface QuestReward {
  gold: number;
  wood: number;
  stone: number;
  mana: number;
  heroXp: number;
  soulShards: number;
  // Hero crystal counts per tier. Optional because some older payloads (and
  // pure-resource quests) omit them entirely; treat missing as 0.
  dimCrystals?: number;
  shiningCrystals?: number;
  ancientCrystals?: number;
}

export interface QuestEntry {
  id: string;
  title: string;
  hint: string;
  progress: number;
  target: number;
  completed: boolean;
  claimed: boolean;
  claimable: boolean;
  reward: QuestReward;
  order: number;
}

export interface DailyChest {
  id: string;
  title: string;
  requiredQuests: number;
  completedQuests: number;
  claimed: boolean;
  claimable: boolean;
  reward: QuestReward;
  order: number;
}

export interface QuestListResponse {
  onboarding: QuestEntry[];
  daily: QuestEntry[];
  dailyChests: DailyChest[];
  dailyResetAtUtc: string;
  tutorialCompleted: boolean;
}

export const questKeys = {
  list: ["quests"] as const,
};

export function useQuests(enabled: boolean = true) {
  return useQuery({
    queryKey: questKeys.list,
    queryFn: async () => {
      const res = await api.get<QuestListResponse>("/quests");
      return res.data;
    },
    enabled,
    // Background refetch — counters bump server-side via action hooks, so
    // the panel updates within ~30s without manual refresh after a battle.
    refetchInterval: 30_000,
  });
}

export function useClaimQuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (questId: string) => {
      const res = await api.post(`/quests/claim/${questId}`);
      return res.data;
    },
    onSuccess: () => {
      // Reward updates castle resources too — invalidate both.
      qc.invalidateQueries({ queryKey: questKeys.list });
      qc.invalidateQueries({ queryKey: ["castle"] });
    },
  });
}

// Claims a daily-quest milestone chest (Bronze / Silver / Gold) — bigger
// HeroXp / Soul Shards rewards for hitting 3/6/10 completed daily quests.
export function useClaimChest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (chestId: string) => {
      const res = await api.post(`/quests/claim-chest/${chestId}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: questKeys.list });
      qc.invalidateQueries({ queryKey: ["castle"] });
    },
  });
}

// Sets the tutorial-completed flag server-side so the intro overlay
// never appears again for this player.
export function useCompleteTutorial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/quests/tutorial-complete");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: questKeys.list }),
  });
}
